import { Application, Controller } from '@hotwired/stimulus';

export type FilterUpdateDetails = {
    /**
     * The quantity of items for the current filter selection
     */
    itemsTotal?: number;
    texts?: FilterTexts;

    /**
     * If you have additional filters from outside,
     * you can force enable the reset button
     */
    forceShowReset?: boolean;
};

export type FilterSelectionDetails = {
    selected: {
        [key: string]: string | boolean;
    };
    options?: FilterController['optionsValue'];
};

export type FilterTexts = {
    entitySingular?: string;
    entityPlural?: string;
    summaryTagFormat?: string;
    summaryFormatEmpty?: string;
    summaryFormat?: string;
};

export class FilterController extends Controller<HTMLFormElement> {
    static targets = [
        'resetTrigger',
        'filterControl',
        'filterSummary',
        'filterTotalSelected',
        'filterTotalSelectedWrapper',
    ];

    static classes = ['initalized'];

    static values = { dispatch: Boolean, options: Object, handleSsr: Boolean };

    static skipSubmit = false;

    declare readonly hasInitalizedClass: boolean;
    declare readonly initalizedClass: string;
    declare readonly hasDispatchValue: boolean;
    declare readonly hasOptionsValue: boolean;
    declare readonly hasFilterSummaryTarget: boolean;
    declare readonly handleSsrValue: boolean;

    /**
     * Optional configuration options for the filter
     */
    declare readonly optionsValue: {
        asyncAction?: string;
        entityName?: {
            singular?: string;
            plural?: string;
        };
    };

    /**
     * All filter elements
     */
    declare readonly filterControlTargets: HTMLInputElement[];

    /**
     * When true, any event should simply be dispatched for
     * handling in another controller
     */
    declare readonly dispatchValue: boolean;

    /**
     * Element containing the text with the current filter selection summary
     */
    declare readonly filterSummaryTarget: HTMLParagraphElement;

    /**
     * Holds the total in the expand/collapse button
     */
    declare readonly filterTotalSelectedTarget: HTMLSpanElement;

    /**
     * Wrapper for the whole total inside the expand/collapse button
     */
    declare readonly filterTotalSelectedWrapperTarget: HTMLSpanElement;

    /**
     * Reset Button
     */
    declare readonly resetTriggerTarget: HTMLButtonElement;

    texts?: FilterTexts;

    static get shouldLoad() {
        return (
            (document.querySelector(`[data-filter-target="filterTotalSelectedWrapper"]`) !== null &&
                document.querySelector('[data-filter-target="filterSummary"]') !== null) ||
            document.querySelector('[data-filter-target="filterControl"]') !== null
        );
    }

    static register(application: Application) {
        application.register('filter', this);
    }

    connect() {
        if (this.hasInitalizedClass) {
            this.element.classList.add(this.initalizedClass);
        }

        if (!this?.handleSsrValue) {
            if (this.hasFilterSummaryTarget) {
                const textsJson = this.filterSummaryTarget.getAttribute('data-texts');
                if (textsJson) {
                    this.texts = JSON.parse(textsJson) as FilterTexts;
                }
            }
        }

        console.info('Filter initalized');
    }

    toggleFilterItem(event: Event) {
        if (event?.preventDefault) {
            event.preventDefault();
        }

        const labels = (event.currentTarget as HTMLInputElement)?.labels;
        if (labels) {
            setTimeout(() => labels.forEach((label) => label.classList.add('klt-c-button--loading')), 120);
        }

        this.update({
            type: 'update',
        });
    }

    reset() {
        if (!this?.handleSsrValue) {
            this.filterControlTargets.forEach((element) => (element.checked = false));
            this.update({
                type: 'reset',
            });
        }
    }

    update(event: Partial<SubmitEvent>) {
        if (this?.handleSsrValue && this.element.closest('turbo-frame')) {
            if (!FilterController.skipSubmit) {
                FilterController.skipSubmit = true;
                // https://discuss.hotwired.dev/t/triggering-turbo-frame-with-js/1622/12
                this.element.dispatchEvent(new CustomEvent('submit', { bubbles: true }));
            }
        } else if (this?.handleSsrValue) {
            if (!FilterController.skipSubmit) {
                FilterController.skipSubmit = true;
                this.element.submit();
            }
        } else if (this.hasDispatchValue && this.dispatchValue) {
            // Only dispatch for other controllers to take over, e.g. the my content controller
            const detail: FilterSelectionDetails = {
                selected: this.selected(),
                options: this.optionsValue,
            };

            this.dispatch(event.type, { detail });
        } else if (this.element.tagName === 'FORM') {
            if (!FilterController.skipSubmit) {
                FilterController.skipSubmit = true;
                this.element.submit();
            }
        } else {
            console.error('Filter is neither a form nor has dispatch enabled. Doing nothing.');
        }

        FilterController.skipSubmit = false;
    }

    /**
     * Hook to be called by other controllers to let the filter
     * know that the content was updated based on the filter selection
     * and that the filter now should update e.g. the footer
     */
    afterUpdate({ detail }: { detail: FilterUpdateDetails }) {
        this.updateHeader(detail);
        this.updateFooter(detail);
    }

    /**
     * Hook to be called by other controllers to let the filter
     * know that there was a change in selection from outside and
     * that the filter should update
     */
    afterSelection() {
        this.update({
            type: 'update',
        });
    }

    updateFooter(detail: FilterUpdateDetails) {
        if (!this?.handleSsrValue && this.hasOptionsValue && this.optionsValue.entityName) {
            const total = detail.itemsTotal || this.selectedFilterControls().length;
            const texts = detail.texts ? { ...this.texts, ...detail.texts } : this.texts;
            const entity = total === 1 ? texts.entitySingular : texts.entityPlural;

            const textVariables = {
                total,
                entity,
            };

            let text: string;

            if (this.selectedFilterControls().length) {
                const filters = this.selectedFilterControls()
                    .map((element) => {
                        const label = element.getAttribute('data-label');
                        return texts.summaryTagFormat.replace('%s', label);
                    })
                    .join(', ');

                text = this.textReplace(texts.summaryFormat, { ...textVariables, filters });
            } else {
                text = this.textReplace(texts.summaryFormatEmpty, textVariables);
            }

            if (this.selectedFilterControls().length || detail?.forceShowReset) {
                this.resetTriggerTarget.hidden = false;
            } else {
                this.resetTriggerTarget.hidden = true;
            }

            this.filterSummaryTarget.innerText = text;
        }
    }

    updateHeader(detail: FilterUpdateDetails) {
        if (!this?.handleSsrValue) {
            const selected = this.selectedFilterControls().length;

            if (selected) {
                this.filterTotalSelectedTarget.innerText = selected.toString();
                this.filterTotalSelectedWrapperTarget.hidden = false;
            } else {
                this.filterTotalSelectedWrapperTarget.hidden = true;
            }
        }
    }

    protected selectedFilterControls() {
        return this.filterControlTargets.filter((element) => element.checked);
    }

    protected selected() {
        if (!this.selectedFilterControls().length) {
            return {};
        }

        return this.selectedFilterControls().reduce((values, input) => ({ ...values, [input.name]: input.value }), {});
    }

    private textReplace(text: string, variables: Record<string, any>) {
        // Search and match all ${variable} placeholders in a string
        const textReplaceRegex = /\${(\w*)}/g;

        return text.replace(textReplaceRegex, (match, key) => {
            if (variables[key] === undefined) {
                return match;
            }

            return variables[key];
        });
    }
}
